import type { IRangeChartConfig, IRangeChartBuild, IRangeChartRange } from './types';

import { merge } from '../../helper';

import addClass from '../../general/class/addClass';

import * as exElement from '../../utils/element';

import { toPercent } from '../utils';

// ----------------------------------------------------------------------------

type TypeMethodsConfig = {
    min: number;
    max: number;
};

// ----------------------------------------------------------------------------

function create(config: IRangeChartConfig = {}): IRangeChartBuild {
    const defConfig: IRangeChartConfig = {};
    merge(defConfig, config);

    const min = defConfig.min ?? 0;
    const max = defConfig.max ?? 100;

    // ------------------------------------------------------------------------

    const build = {
        elements: {
            ranges: [],
            marker: null,
            label: null,
        },
    } as unknown as IRangeChartBuild;

    // ------------------------------------------------------------------------

    const elWrapper = exElement.create({
        merge: defConfig,
        class: {
            new: 'range-chart',
        },
    });
    build.element = elWrapper;
    build.elements.wrapper = elWrapper;

    if (defConfig.vertical === true) {
        addClass(elWrapper, 'range-chart-vertical');
    }

    // ------------------------------------------------------------------------

    if (defConfig.label != null) {
        const elLabel = exElement.create({
            merge: typeof defConfig.label === 'string' ? { html: defConfig.label } : defConfig.label,
            class: {
                new: 'range-chart-label',
            },
        });
        build.elements.label = elLabel;
        elWrapper.appendChild(elLabel);
    }

    // ------------------------------------------------------------------------

    const elTrack = exElement.create({
        class: {
            new: 'range-chart-track',
        },
    });
    build.elements.track = elTrack;
    elWrapper.appendChild(elTrack);

    // ------------------------------------------------------------------------

    if (Array.isArray(defConfig.ranges) && defConfig.ranges.length > 0) {
        let accumulated = 0;

        for (const range of defConfig.ranges as IRangeChartRange[]) {
            const rangePercent = toPercent(range.value, min, max);
            const width = rangePercent - accumulated;
            const left = accumulated;
            accumulated = rangePercent;

            const elRange = exElement.create({
                merge: range,
                class: {
                    new: 'range-chart-range',
                },
                style: {
                    left: left + '%',
                    width: width + '%',
                },
            });

            if (typeof range.color === 'string') {
                addClass(elRange, 'range-chart-range-' + range.color);
            }

            build.elements.ranges.push(elRange);
            elTrack.appendChild(elRange);
        }
    }

    // ------------------------------------------------------------------------

    const elBar = exElement.create({
        class: {
            new: 'range-chart-bar',
        },
        style: {
            width: defConfig.value != null ? toPercent(defConfig.value, min, max) + '%' : '0%',
        },
    });
    build.elements.bar = elBar;
    elTrack.appendChild(elBar);

    // ------------------------------------------------------------------------

    if (defConfig.target != null) {
        const elMarker = exElement.create({
            class: {
                new: 'range-chart-marker',
            },
            style: {
                left: toPercent(defConfig.target, min, max) + '%',
            },
        });
        build.elements.marker = elMarker;
        elTrack.appendChild(elMarker);
    }

    // ------------------------------------------------------------------------

    // Methods
    methods(build, { min, max });

    // ------------------------------------------------------------------------

    return build;
}

function methods(build: IRangeChartBuild, config: TypeMethodsConfig): void {
    const { min, max } = config;
    const elTrack = build.elements.track;

    build.setValue = function (value: number): void {
        const percent = toPercent(value, min, max);
        build.elements.bar.style.width = percent + '%';
    };

    build.setTarget = function (value: number): void {
        if (build.elements.marker == null) {
            const elMarker = exElement.create({
                class: {
                    new: 'range-chart-marker',
                },
            });
            build.elements.marker = elMarker;
            elTrack.appendChild(elMarker);
        }

        const percent = toPercent(value, min, max);
        build.elements.marker.style.left = percent + '%';
    };
}

// ----------------------------------------------------------------------------

export { create };
